export enum AppPlatform {
  iOS = "ios",
  Android = "android",
  Amazon = "amazon",
  Web = "web",
}

export enum AnalyticsEventType {
  ACTIVE = "ACTIVE",
  BACKGROUND = "BACKGROUND",
  PAGE_VIEW = "PAGE_VIEW",
  PARENT_GATE_BTN_CLICK = "PARENT_GATE_CLICK",
  PARENT_GATE_SUCCESS_BTN_CLICK = "PARENT_GATE_SUCCESS_BTN_CLICK",
  PARENT_GATE_FAIL = "PARENT_GATE_FAIL",
  PARENT_GATE_MENU_NOTIFICATION_CLICK = "PARENT_GATE_MENU_NOTIFICATION_CLICK",
  PARENT_GATE_MENU_PARENT_USER_NOT_SIGN = "PARENT_GATE_MENU_PARENT_USER_NOT_SIGN",
  PARENT_GATE_MENU_PARENT_USER_CLICK = "PARENT_GATE_MENU_PARENT_USER_CLICK",
  PARENT_GATE_MENU_PARENT_APP_CLICK = "PARENT_GATE_MENU_PARENT_APP_CLICK",
  PARENT_GATE_MENU_ENABLE_LAB_CLICK = "PARENT_GATE_MENU_ENABLE_LAB_CLICK",
  UNIVERSAL_BTN_CLICK = "UNIVERSAL_BTN_CLICK",
  SUBSCRIPTION_PURCHASE_BTN_CLICK = "SUBSCRIPTION_PURCHASE_BTN_CLICK",
  UPGRADE_PROMOTION_CHANGE_BTN_CLICK = "UPGRADE_PROMOTION_CHANGE_BTN_CLICK",
}

export enum GameCase {
  GRAY = "gray",
  BLUE = "blue",
  CYAN = "cyan",
  MILK = "milk",
  PINK = "pink",
  GREEN = "green",
  ORANGE = "orange",
  SOFT_PINK = "soft_pink",
  SOFT_YELLOW = "soft_yellow",
}

export enum Character {
  DUCK = "duck",
  BEAR = "bear",
  DOG = "dog",
}

export enum CourseModel {
  COMMAND = "command",
  TANGIBLE = "tangible",
}

export enum DeviceNotification {
  NOT_DETERMINED = "NOT_DETERMINED",
  AUTHORIZED = "AUTHORIZED",
  DENIED = "DENIED",
}

export enum LocalStorageKey {
  LAST_SIGN_AT = "last_sign_at",
  ACTIVE_USER_UID = "active_user_uid",
  LAST_ACTIVE_USER_UID = "last_active_user_uid",
  DISMISS_BANNER = "dismiss_banner",
  DISMISS_SURVEY_BANNER = "dismiss_survey_banner",
  KEYBOARD_HEIGHT = "keyboard_height",
}

export interface UserConfig {
  is_first: boolean;
  locale: string | null;
  sound: { enable: boolean; volume: number };
  bgm: { enable: boolean; volume: number };
  tutorial: { top: boolean; creator_top: boolean; creator_panel: boolean };
  game_case: GameCase;
  character: Character;
  project_publish: boolean;
  project_copied: boolean;
  enable_lab: boolean;
  course_model: CourseModel;
  ai_chat: boolean;
  ai_generation: boolean;
}

export enum EventType {
  BEFORE_INSTALL_PROMPT = "before_install_prompt",
}

export type Asset = {
  id: number;
  uid: string;
  type: string;
  name: string;
  tags: string;
  content_type: string;
  file_size: number;
  metadata: { width: string; height: string; [key: string]: string };
  url: string;
  created_at: string;
};

export enum DialogType {
  RATING = "rating",
  CONTACT = "contact",
  LOADING = "loading",
  FEEDBACK = "feedback",
  UNIVERSAL = "universal",
  ASSISTANT = "assistant",
  PARENT_GATE = "parent_gate",
  USER_PROFILE = "user_profile",
  NOTIFICATION = "notification",
  ERROR_NETWORK = "error_network",
  PROJECT_PREVIEW = "project_preview",
  PWA_INSTALL_PROMPT = "pwa_install_prompt",
  UPGRADE_PROMOTION = "upgrade_promotion",
  RESTRICTED_FEATURE = "restricted_feature",
}

export enum RestrictedFeatureSource {
  // コース制限（TRIAL: stage 3以降）
  COURSE = "course",
  // プロジェクト制限（全プラン: TRIAL=1, FREE=5, STANDARD=10, PREMIUM=10）
  PROJECT_LIMIT = "project_limit",
  // ユーザー追加制限（全プラン: TRIAL=1, FREE=2, STANDARD=4, PREMIUM=8）
  CHILD_USER_LIMIT = "child_user_limit",
  // 体験ユーザーのプレイヤー追加制限（TRIAL のみ）
  CHILD_USER_ADD_TRIAL = "child_user_add_trial",
  // 保護者設定（TRIAL のみ）
  PARENT_SETTINGS = "parent_settings",
  // AI チャット制限達成時（TRIAL=日4回, FREE=日8回, EDUCATION=使用不可）
  AI_CHAT_LIMIT = "ai_chat_limit",
  // AI 画像生成制限達成時（TRIAL=日1回, FREE=日2回, STANDARD=日10回, EDUCATION=使用不可）
  AI_IMAGE_LIMIT = "ai_image_limit",
  // チャット画像（PREMIUM 機能）
  CHAT_IMAGE = "chat_image",
  // 素材制限達成時（TRIAL=50, FREE=100, EDUCATION=50, STANDARD以上=無制限）
  ASSET_LIMIT = "asset_limit",
  // 体験ユーザー向けログイン促進（7日以上経過したGUEST）
  GUEST_LOGIN_PROMPT = "guest_login_prompt",
}

export interface ConfigState {
  loading: boolean;
  parentConfig: UserConfig;
  userConfig: UserConfig;
  tutorial: { top: boolean }; // チュートリアルが閉じているかどうか状態を保存する。
  notification: {
    kids: {
      id: number;
      ja: { date: string; title: string; content: string };
      ja_kanji: { date: string; title: string; content: string };
      zh_tw: { date: string; title: string; content: string };
      zh_cn: { date: string; title: string; content: string };
      en: { date: string; title: string; content: string };
      published_at: string;
    }[];
    parents: {
      id: number;
      ja_kanji: { date: string; title: string; content: string };
      zh_tw: { date: string; title: string; content: string };
      zh_cn: { date: string; title: string; content: string };
      en: { date: string; title: string; content: string };
      published_at: string;
    }[];
    maintenances: {
      id: number;
      ja: { date: string; title: string; content: string };
      zh_tw: { date: string; title: string; content: string };
      zh_cn: { date: string; title: string; content: string };
      en: { date: string; title: string; content: string };
      created_at: string;
      published_at: string;
    }[];
  };
  banned: { words: { id: number; word: string }[] };
  assets: {
    [prefix: string]: {
      fileKey: string;
      fileSize: number;
      category?: string;
    }[];
  };
  event: {
    before_install_prompt: { value: boolean; args?: { [key: string]: string } };
  };
  dialog: {
    rating: { value: boolean; args?: { [key: string]: string } };
    contact: { value: boolean; args?: { [key: string]: string } };
    loading: { value: boolean; args?: { [key: string]: string } };
    feedback: { value: boolean; args?: { [key: string]: string } };
    assistant: { value: boolean; args?: { [key: string]: string } };
    universal: { value: boolean; args?: { [key: string]: string } };
    parent_gate: { value: boolean; args?: { [key: string]: string } };
    user_profile: { value: boolean; args?: { [key: string]: string } };
    notification: { value: boolean; args?: { [key: string]: string } };
    error_network: { value: boolean; args?: { [key: string]: string } };
    project_preview: { value: boolean; args?: { [key: string]: string } };
    pwa_install_prompt: { value: boolean; args?: { [key: string]: string } };
    upgrade_promotion: { value: boolean; args?: { [key: string]: string } };
    restricted_feature: { value: boolean; args?: { [key: string]: string } };
  };
  justAuthenticated: boolean;
}
